import { doc, getDoc, getDocs, limit, orderBy, query } from "firebase/firestore";
import { businessCollection } from "./firestorePaths";
import { db } from "@lib/firebase";

// Transactions are written by the Traka POS app to
// businesses/{businessId}/transactions/{transactionId}. The stored shape is the
// Android Transaction entity, which does not match what the table renders, so
// everything is normalized here and the view layer stays unchanged.

// Newest first, and capped: a busy till produces thousands of these and the
// table has no server-side paging yet.
const MAX_TRANSACTIONS = 500;

const toStringSafe = (value) => (typeof value === "string" ? value.trim() : "");
const toNumberSafe = (value, fallback = 0) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  // Amounts arrive as strings on payments (PaymentModel.amount is a String).
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

// The POS writes "complete"; TRANSACTION_STATUS is keyed "completed", so an
// unmapped value would render as "Unknown" on every row.
const STATUS_ALIASES = {
  complete: "completed",
  completed: "completed",
  void: "voided",
  voided: "voided",
  cancelled: "voided",
  pending: "pending",
};

function transactionStatus(raw) {
  const key = toStringSafe(raw).toLowerCase();
  return STATUS_ALIASES[key] || key || "completed";
}

function customerName(customer = {}) {
  const name = [toStringSafe(customer.firstName), toStringSafe(customer.lastName)]
    .filter(Boolean)
    .join(" ");
  return name || toStringSafe(customer.email);
}

function normalizePayment(payment = {}) {
  return {
    method: toStringSafe(payment.methodLabel) || toStringSafe(payment.method),
    amount: toNumberSafe(payment.amount),
    reference: toStringSafe(payment.gatewayReference),
  };
}

/**
 * Firestore transaction document -> the row shape the table renders.
 *
 * `staff` maps transactor uid -> user, so the row can show a cashier name
 * instead of a uid.
 */
function normalizeTransaction(id, txn = {}, staff = {}) {
  const totals = txn.totals || {};
  const items = Array.isArray(txn.items) ? txn.items : [];
  const returns = Array.isArray(txn.returns) ? txn.returns : [];
  const payments = (Array.isArray(txn.payments) ? txn.payments : []).map(normalizePayment);

  const total = toNumberSafe(totals.total);
  const amountPaid = payments.reduce((acc, p) => acc + p.amount, 0);
  const createdAt = toNumberSafe(txn.createdAt) || toNumberSafe(txn.metadata?.createdAt);
  const name = customerName(txn.customer);

  return {
    id,
    // No separate human-facing number is stored; the document id is the receipt id.
    transactionNumber: toStringSafe(txn.transactionId) || id,
    date: createdAt ? new Date(createdAt).toISOString() : "",
    // The POS records returns as line entries rather than a type flag.
    type: returns.length > 0 ? "return" : "sale",
    status: transactionStatus(txn.status),
    customer: name ? { id: toStringSafe(txn.customer?.email), name } : null,
    location: toStringSafe(txn.locationId),
    // No counter is recorded on the transaction itself - the counters
    // collection is separate and not referenced from here.
    counter: "",
    cashier: staff[txn.transactor]?.name || toStringSafe(txn.transactor),
    itemsCount: items.reduce((acc, item) => acc + toNumberSafe(item.quantity, 1), 0),
    subTotal: toNumberSafe(totals.subtotal),
    taxAmount: toNumberSafe(totals.tax),
    discount: toNumberSafe(totals.discount),
    currency: toStringSafe(totals.currency),
    total,
    payments,
    items,
    notes: toStringSafe(txn.notes),
    meta: {
      amountPaid,
      balanceDue: Math.max(0, total - amountPaid),
    },
  };
}

export async function fetchTransactionsList(businessId, staff = {}) {
  if (!businessId) return [];

  const snap = await getDocs(
    query(
      businessCollection(businessId, "transactions"),
      orderBy("createdAt", "desc"),
      limit(MAX_TRANSACTIONS)
    )
  );

  return snap.docs.map((d) => normalizeTransaction(d.id, d.data(), staff));
}

export async function fetchTransactionById(businessId, id, staff = {}) {
  if (!businessId || !id) return null;
  const snap = await getDoc(doc(db, "businesses", businessId, "transactions", id));
  return snap.exists() ? normalizeTransaction(snap.id, snap.data(), staff) : null;
}
